import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../auth';
import { currencySymbol, formatMoneyCompact } from '../../shared/money';
import { loadStripe } from '@stripe/stripe-js';
import {
  CardElement,
  Elements,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';

const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '';
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;

interface TipAmount {
  value: number;
  label: string;
}

interface PaymentMethodInfo {
  brand: string;
  last4: string;
}

interface TipTarget {
  id: string;
  displayName: string;
  photoUrl?: string | null;
}

interface TipFormProps {
  modelProfileId: string;
  modelName: string;
  roomId?: string;
  /** Event currency (ISO 4217). Tips charge in the event's currency; defaults to USD. */
  currency?: string;
  onSuccess?: () => void;
  onClose?: () => void;
  presetAmounts?: TipAmount[];
}

interface TipFormsProps {
  models: TipTarget[];
  roomId?: string;
  /** Event currency (ISO 4217). Tips charge in the event's currency; defaults to USD. */
  currency?: string;
  onSuccess?: () => void;
  onClose?: () => void;
  presetAmounts?: TipAmount[];
}

const DEFAULT_PRESET_CENTS = [300, 500];

function defaultPresetAmounts(currency: string | undefined): TipAmount[] {
  return DEFAULT_PRESET_CENTS.map((value) => ({
    value,
    label: formatMoneyCompact(value, currency),
  }));
}

function TipPaymentMethodPanel({
  paymentMethod,
  checkingCard,
  cardComplete,
  savingCard,
  cardError,
  isSignedIn,
  onSaveCard,
  onCardChange,
}: {
  paymentMethod: PaymentMethodInfo | null;
  checkingCard: boolean;
  cardComplete: boolean;
  savingCard: boolean;
  cardError: string | null;
  isSignedIn: boolean | undefined;
  onSaveCard: () => void;
  onCardChange: (complete: boolean, error: string | null) => void;
}) {
  if (checkingCard) {
    return null;
  }

  if (paymentMethod) {
    return null;
  }

  if (!isSignedIn) {
    return null;
  }

  return (
    <div className="bg-panel border border-line rounded-xl p-5">
      <div className="mb-4">
        <h3 className="font-display text-lg font-normal">Add a card</h3>
        <p className="text-sm text-text-dim mt-1">
          Add a card once, then choose a tip amount for any model from this session.
        </p>
      </div>
      <label className="block text-[10px] text-text-faint font-mono uppercase mb-2">
        Card details
      </label>
      <div className="p-3 rounded-lg bg-white border border-line">
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#1a1a1a',
                '::placeholder': { color: '#9ca3af' },
              },
              invalid: { color: '#ef4444' },
            },
          }}
          onChange={(event) => onCardChange(event.complete, event.error?.message ?? null)}
        />
      </div>

      {cardError && (
        <p className="text-sm text-warn mt-3">{cardError}</p>
      )}

      <button
        type="button"
        onClick={onSaveCard}
        disabled={savingCard || !cardComplete}
        className="btn btn-accent w-full mt-4"
      >
        {savingCard ? 'Saving card...' : 'Save card for tips'}
      </button>
    </div>
  );
}

function TipPanel({
  model,
  roomId,
  currency,
  canTip,
  paymentMethodReady,
  getToken,
  onSuccess,
  onClose,
  presetAmounts,
}: {
  model: TipTarget;
  roomId?: string;
  currency?: string;
  canTip: boolean;
  paymentMethodReady: boolean;
  getToken: () => Promise<string | null>;
  onSuccess?: () => void;
  onClose?: () => void;
  presetAmounts?: TipAmount[];
}) {
  const presets = presetAmounts ?? defaultPresetAmounts(currency);
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const getAmountInCents = (): number | null => {
    if (!isCustom) return selectedAmount;

    const parsed = parseFloat(customAmount);
    if (isNaN(parsed) || parsed < 1) return null;
    return Math.round(parsed * 100);
  };

  const createTip = async () => {
    const token = await getToken();
    const amountCents = getAmountInCents();
    if (!amountCents) {
      throw new Error('Please select or enter an amount');
    }

    const response = await fetch('/api/tips', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        modelProfileId: model.id,
        amount: amountCents,
        roomId,
      }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to process tip');
    }

    return response.json();
  };

  const handleSubmit = async () => {
    if (!canTip) {
      setError(paymentMethodReady ? 'Please sign in to send a tip' : 'Add a card before sending a tip');
      return;
    }

    const amountCents = getAmountInCents();
    if (!amountCents) {
      setError('Please select or enter an amount');
      return;
    }

    if (amountCents < 100) {
      setError(`Minimum tip is ${formatMoneyCompact(100, currency)}`);
      return;
    }

    if (amountCents > 100000) {
      setError(`Maximum tip is ${formatMoneyCompact(100000, currency)}`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await createTip();

      if (result.requiresPayment) {
        throw new Error('Add a card before sending a tip.');
      }

      setSuccess(true);
      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send tip');
    } finally {
      setLoading(false);
    }
  };

  const amountCents = getAmountInCents();
  const amountLabel = amountCents ? formatMoneyCompact(amountCents, currency) : '';

  if (success) {
    return (
      <div className="bg-panel border border-line rounded-xl p-6 text-center">
        <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-accent-soft flex items-center justify-center">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-accent">
            <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <h3 className="font-display text-lg font-normal mb-1">Tip sent</h3>
        <p className="text-sm text-text-dim">
          Thank you for supporting {model.displayName}.
        </p>
        {onClose && (
          <button onClick={onClose} className="btn btn-sm mt-4">
            Done
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="bg-panel border border-line rounded-xl p-5">
      <div className="flex items-start gap-3 mb-4">
        {model.photoUrl && (
          <img
            src={model.photoUrl}
            alt=""
            className="w-11 h-11 rounded-full object-cover border border-line"
          />
        )}
        <div>
          <h3 className="font-display text-xl font-normal">Tip {model.displayName}</h3>
          {!paymentMethodReady && (
            <p className="text-sm text-text-dim mt-1">Add a card above to send a tip.</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-4">
        {presets.map(({ value, label }) => (
          <button
            key={value}
            type="button"
            onClick={() => {
              setSelectedAmount(value);
              setIsCustom(false);
              setCustomAmount('');
              setError(null);
            }}
            className={`h-12 px-3 rounded border text-base font-semibold shadow-sm transition-all ${
              selectedAmount === value && !isCustom
                ? 'bg-accent border-accent text-white'
                : 'bg-white border-line text-black hover:border-text/40 hover:bg-white hover:shadow-md'
            }`}
          >
            {label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => {
            setIsCustom(true);
            setError(null);
          }}
          className={`h-12 px-3 rounded border text-base font-semibold shadow-sm transition-all ${
            isCustom
              ? 'bg-accent border-accent text-white'
              : 'bg-white border-line text-black hover:border-text/40 hover:bg-white hover:shadow-md'
          }`}
        >
          Custom
        </button>
      </div>

      {isCustom && (
        <div className="relative mb-4">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-dim">{currencySymbol(currency)}</span>
          <input
            type="number"
            min="1"
            max="1000"
            step="1"
            value={customAmount}
            onChange={(e) => setCustomAmount(e.target.value)}
            placeholder="Enter amount"
            className={`form-input w-full ${currencySymbol(currency).length > 1 ? 'pl-14' : 'pl-7'}`}
            autoFocus
          />
        </div>
      )}

      {error && (
        <p className="text-sm text-warn mb-3">{error}</p>
      )}

      {amountCents && (
        <button
          type="button"
          onClick={handleSubmit}
          disabled={loading || !canTip}
          className="btn btn-accent w-full"
        >
          {loading ? 'Charging...' : `Submit tip${amountLabel ? ` ${amountLabel}` : ''}`}
        </button>
      )}
    </div>
  );
}

function TipFormsInner({
  models,
  roomId,
  currency,
  onSuccess,
  onClose,
  presetAmounts,
}: TipFormsProps) {
  const { getToken, isSignedIn } = useAuth();
  const stripe = useStripe();
  const elements = useElements();
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodInfo | null>(null);
  const [checkingCard, setCheckingCard] = useState(true);
  const [cardComplete, setCardComplete] = useState(false);
  const [savingCard, setSavingCard] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);

  const refreshPaymentMethod = useCallback(async () => {
    if (!isSignedIn) {
      setPaymentMethod(null);
      setCheckingCard(false);
      return;
    }

    setCheckingCard(true);
    try {
      const token = await getToken();
      const response = await fetch('/api/stripe/payment-method-status', {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setPaymentMethod(data.paymentMethod || null);
      }
    } catch (err) {
      console.error('Error checking payment method:', err);
    } finally {
      setCheckingCard(false);
    }
  }, [getToken, isSignedIn]);

  useEffect(() => {
    void refreshPaymentMethod();
  }, [refreshPaymentMethod]);

  const saveCard = async () => {
    if (!stripe || !elements) {
      setCardError('Payment form is still loading');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setCardError('Please enter your card details');
      return;
    }

    setSavingCard(true);
    setCardError(null);

    try {
      const token = await getToken();
      const setupResponse = await fetch('/api/stripe/setup-intent', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!setupResponse.ok) {
        const data = await setupResponse.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to prepare card');
      }

      const { clientSecret } = await setupResponse.json();
      const { setupIntent, error: stripeError } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card: cardElement },
      });

      if (stripeError) {
        throw new Error(stripeError.message || 'Card setup failed');
      }

      if (!setupIntent?.payment_method) {
        throw new Error('No payment method returned');
      }

      const setDefaultResponse = await fetch('/api/stripe/set-default-payment-method', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ paymentMethodId: setupIntent.payment_method }),
      });

      if (!setDefaultResponse.ok) {
        throw new Error('Failed to save payment method');
      }

      setCardComplete(false);
      await refreshPaymentMethod();
    } catch (err) {
      setCardError(err instanceof Error ? err.message : 'Failed to save card');
    } finally {
      setSavingCard(false);
    }
  };

  const paymentMethodReady = Boolean(paymentMethod);
  const canTip = Boolean(isSignedIn && paymentMethodReady && !checkingCard);

  return (
    <section className="space-y-4">
      <div className="text-center">
        <h2 className="font-display text-2xl font-normal">Tip the model{models.length === 1 ? '' : 's'}</h2>
        <p className="text-sm text-text-dim mt-1">
          Life Drawing Live does not charge platform fees on tips.
        </p>
      </div>

      <TipPaymentMethodPanel
        paymentMethod={paymentMethod}
        checkingCard={checkingCard}
        cardComplete={cardComplete}
        savingCard={savingCard}
        cardError={cardError}
        isSignedIn={isSignedIn}
        onSaveCard={saveCard}
        onCardChange={(complete, error) => {
          setCardComplete(complete);
          setCardError(error);
        }}
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {models.map((model) => (
          <TipPanel
            key={model.id}
            model={model}
            roomId={roomId}
            currency={currency}
            canTip={canTip}
            paymentMethodReady={paymentMethodReady}
            getToken={getToken}
            onSuccess={onSuccess}
            onClose={onClose}
            presetAmounts={presetAmounts}
          />
        ))}
      </div>
    </section>
  );
}

export function TipForms(props: TipFormsProps) {
  if (!props.models.length) return null;

  if (!stripePromise) {
    return (
      <div className="bg-panel border border-line rounded-xl p-5">
        <h3 className="font-display text-xl font-normal">Tips</h3>
        <p className="text-sm text-text-dim mt-2">Stripe is not configured for this environment.</p>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <TipFormsInner {...props} />
    </Elements>
  );
}

export function TipForm(props: TipFormProps) {
  return (
    <TipForms
      models={[{
        id: props.modelProfileId,
        displayName: props.modelName,
      }]}
      roomId={props.roomId}
      currency={props.currency}
      onSuccess={props.onSuccess}
      onClose={props.onClose}
      presetAmounts={props.presetAmounts}
    />
  );
}
